import React from 'react';
import { useUser, SignInButton } from '@clerk/clerk-react';
import { SEOHead } from '../lib/seo';
import { useQuery } from 'convex/react';
import { api } from '../lib/convex';
import { Check, Crown, Zap, Star, Clock, LightbulbIcon, BrainCircuit, Calculator, DollarSign, BadgeAlert, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Footerdemo } from './ui/footer-section';
import SubscriptionManager from './SubscriptionManager';
import { SERVICE_TIERS } from '../../convex/constants/serviceTiers';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function Upgrade() {
  const { user, isLoaded, isSignedIn } = useUser();
  const preferences = useQuery(api.posts.getUserPreferences);
  
  const navigate = useNavigate();
  
  // Check if user has a premium plan
  const userTier = preferences?.serviceTier || user?.privateMetadata?.serviceTier || 'free';
  const isPremium = userTier !== 'free';
  
  // If user is signed in with premium, redirect to writer page
  React.useEffect(() => {
    if (isLoaded && isSignedIn && isPremium) {
      navigate('/writer');
    }
  }, [isLoaded, isSignedIn, isPremium, navigate]);

  return (
    <div className="min-h-screen flex flex-col pt-16 bg-cream">
      <SEOHead
        title="Upgrade Your Content Creation - Pro & Enterprise Plans"
        description="Stop wasting time on AI prompts that produce robotic content. Upgrade to AISEOBlogGenerator Pro or Enterprise and get access to premium AI models, unlimited writing styles, and content that actually converts."
        type="website"
        canonicalUrl="https://aiseobloggenerator.com/upgrade"
      />
      <div className="flex-1 px-4 py-12">
        {/* Hero Section */}
        <section className="max-w-5xl mx-auto mb-16">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4 border border-blue-200">FOUNDER PRICING - LIMITED TIME OFFER</span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Stop Wasting Hours on AI Prompts That <span className="text-dogbrown">Still Sound Like AI</span></h1>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              You've already spent way too much time trying to make ChatGPT sound like a human. It's time to stop the madness.
            </p>
          </div>

          {/* Problem Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-16 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-100 p-3 rounded-full">
                <BadgeAlert className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold">The Problem Is Costing You More Than You Think</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Time Drain</h3>
                    <p className="text-gray-700">
                      Marketers and founders spend an average of 47 minutes per post trying to craft the "perfect" prompt, then another 30+ minutes editing the robotic-sounding results.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Hidden AI Costs</h3>
                    <p className="text-gray-700">
                      Premium AI models like o3, o1-pro, and Claude 3.7 cost $20-$250/month when purchased directly - just for basic access!
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <BrainCircuit className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">AI Content Sounds Like... AI</h3>
                    <p className="text-gray-700">
                      Most AI content still sounds robotic and filled with painful phrases like "In today's fast-paced digital landscape" and "unlock the full potential."
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Opportunity Cost</h3>
                    <p className="text-gray-700">
                      What's your hourly rate? $50? $100? $250+? Every hour spent wrestling with AI prompts is money lost that could be driving actual business growth.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Agitation Section */}
          <div className="bg-gradient-to-r from-dogblue to-blue-700 text-white rounded-2xl shadow-xl p-8 mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center">What's This Actually Costing You?</h2>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8">
              <div className="grid md:grid-cols-4 gap-4 text-center">
                <div className="p-4">
                  <p className="text-sm text-blue-200 mb-1">Time Per Post</p>
                  <p className="text-3xl font-bold">77 min</p>
                  <p className="text-xs text-blue-200 mt-1">Prompt + Editing</p>
                </div>
                
                <div className="p-4">
                  <p className="text-sm text-blue-200 mb-1">Hourly Value</p>
                  <p className="text-3xl font-bold">$100</p>
                  <p className="text-xs text-blue-200 mt-1">Conservative Est.</p>
                </div>
                
                <div className="p-4">
                  <p className="text-sm text-blue-200 mb-1">Posts Per Month</p>
                  <p className="text-3xl font-bold">10</p>
                  <p className="text-xs text-blue-200 mt-1">Typical Marketing</p>
                </div>
                
                <div className="p-4 bg-white/20 rounded-lg">
                  <p className="text-sm text-white mb-1">Monthly Cost</p>
                  <p className="text-3xl font-bold text-yellow-300">$1,283</p>
                  <p className="text-xs text-white mt-1">Time + Premium AI</p>
                </div>
              </div>
            </div>
            
            <p className="text-lg text-center max-w-4xl mx-auto leading-relaxed">
              If you're spending over $1,200 a month in time and tools trying to create content that <span className="italic">still</span> sounds like it was written by a robot. And that doesn't even count the opportunity cost of what you <span className="font-bold">could</span> be doing with those hours instead.
            </p>
          </div>

          {/* Solution Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-100 p-3 rounded-full">
                <LightbulbIcon className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold">The Solution: AISEOBlogGenerator</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-600" />
                  Save 80% of Your Time
                </h3>
                <p className="text-gray-700">
                  Generate a complete, high-quality blog post in under 10 minutes, with no prompt engineering needed.
                </p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <BrainCircuit className="w-5 h-5 text-green-600" />
                  Top-Tier AI Models
                </h3>
                <p className="text-gray-700">
                  Get access to premium models like Claude-3.7-Sonnet, o1 and o1-mini at a fraction of the retail cost.
                </p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  10x ROI Minimum
                </h3>
                <p className="text-gray-700">
                  For less than the cost of 1 hour of your time, get a month's worth of content that doesn't sound like AI.
                </p>
              </div>
            </div>
            
            <div className="text-center mb-12">
              <h3 className="text-xl font-bold mb-4">What People Are Saying</h3>
              <div className="max-w-2xl mx-auto bg-gray-50 p-6 rounded-lg border border-gray-200 italic text-gray-700">
                "I used to spend 1-2 hours per blog post trying to make ChatGPT sound like a human. Now I click a button and get better content in 5 minutes. This tool literally pays for itself the first time you use it."
                <div className="mt-4 font-normal text-sm text-gray-500">— Michael S., Marketing Director</div>
              </div>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-dogblue" />
                <p className="text-dogblue font-medium">
                  Our pricing is 80% off for early adopters — Founder Pricing won't last forever!
                </p>
              </div>
              <p className="text-dogblue font-bold">
                Use code <span className="bg-blue-100 px-2 py-1 rounded font-mono">FOUNDER25</span> at checkout for a FREE 30-day trial!
              </p>
            </div>
          </div>
        </section>
        
        {/* The actual pricing section/table */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-4">Choose Your Plan</h2>
            <p className="text-gray-700">
              Select the plan that works best for your content needs
            </p>
          </div>

          {/* Pricing Cards - Styled to match the subscription manager component */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {/* Pro Plan */}
            <motion.div 
              className="rounded-xl shadow-lg overflow-hidden border bg-white relative"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              {/* Founder pricing banner */}
              <div className="bg-gradient-to-r from-dogbrown to-amber-600 text-white py-2 text-center shadow-sm">
                <span className="text-xs font-bold uppercase tracking-wider">Founder Launch Pricing</span>
              </div>
              
              <div className="p-6 pt-8">
                <div className="flex items-start">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-dogblue" />
                      <span>Pro</span>
                    </h3>
                    
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-baseline">
                        <span className="text-3xl font-extrabold text-dogblue">
                          $9.95
                        </span>
                        <span className="text-base font-medium text-gray-500 ml-1">
                          /month
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <s className="text-gray-400 text-sm">Regular: $24.95/month</s>
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Save 60%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-blue-700">P</span>
                  </div>
                </div>

                <ul className="mt-6 space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-blue-100">
                      <Check className="w-3 h-3 text-blue-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      <strong>15</strong> posts per month
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-blue-100">
                      <Check className="w-3 h-3 text-blue-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      <strong>4,000</strong> words per post
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-blue-100">
                      <Check className="w-3 h-3 text-blue-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Access to advanced AI models
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-blue-100">
                      <Check className="w-3 h-3 text-blue-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Custom writing styles
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-blue-100">
                      <Check className="w-3 h-3 text-blue-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Strike list feature
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-blue-100">
                      <Check className="w-3 h-3 text-blue-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Marketer mode
                    </span>
                  </li>
                </ul>

                {!isSignedIn ? (
                  <SignInButton mode="modal">
                    <Button className="mt-8 w-full bg-dogblue hover:bg-dogblue/90 text-white">
                      Sign in to upgrade
                    </Button>
                  </SignInButton>
                ) : (
                  <Link to="/writer">
                    <Button className="mt-8 w-full bg-dogblue hover:bg-dogblue/90 text-white">
                      Get Started
                    </Button>
                  </Link>
                )}
              </div>
              <div className="py-3 px-6 bg-gray-50 text-gray-500 text-sm text-center">
                Limited time offer - regular price will apply after launch period
              </div>
            </motion.div>
            
            {/* Enterprise Plan */}
            <motion.div 
              className="rounded-xl shadow-lg overflow-hidden border bg-white relative"
              whileHover={{ 
                y: -5,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              {/* Founder pricing banner */}
              <div className="bg-gradient-to-r from-dogbrown to-amber-600 text-white py-2 text-center shadow-sm">
                <span className="text-xs font-bold uppercase tracking-wider">Founder Launch Pricing</span>
              </div>
              
              <div className="p-6 pt-8">
                <div className="flex items-start">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-dogbrown" />
                      <span>Enterprise</span>
                    </h3>
                    
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-baseline">
                        <span className="text-3xl font-extrabold text-dogbrown">
                          $49.95
                        </span>
                        <span className="text-base font-medium text-gray-500 ml-1">
                          /month
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <s className="text-gray-400 text-sm">Regular: $110/month</s>
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Save 55%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-amber-700">E</span>
                  </div>
                </div>

                <ul className="mt-6 space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-amber-100">
                      <Check className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      <strong>100</strong> posts per month
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-amber-100">
                      <Check className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      <strong>4,000</strong> words per post
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-amber-100">
                      <Check className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Access to all AI models
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-amber-100">
                      <Check className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Custom writing styles
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-amber-100">
                      <Check className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Strike list feature
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-amber-100">
                      <Check className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Marketer mode
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center bg-amber-100">
                      <Check className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Priority support
                    </span>
                  </li>
                </ul>

                {!isSignedIn ? (
                  <SignInButton mode="modal">
                    <Button className="mt-8 w-full bg-dogbrown hover:bg-dogbrown/90 text-white">
                      Sign in to upgrade
                    </Button>
                  </SignInButton>
                ) : (
                  <Link to="/writer">
                    <Button className="mt-8 w-full bg-dogbrown hover:bg-dogbrown/90 text-white">
                      Get Started
                    </Button>
                  </Link>
                )}
              </div>
              <div className="py-3 px-6 bg-gray-50 text-gray-500 text-sm text-center">
                Limited time offer - regular price will apply after launch period
              </div>
            </motion.div>
          </div>

          {/* Show the full Subscription Manager for authenticated users */}
          {isSignedIn && (
            <div className="mt-16 pt-8 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-center mb-8">Manage Your Subscription</h2>
              <SubscriptionManager />
            </div>
          )}

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto mt-16 bg-white rounded-xl p-8 border border-gray-200 shadow-md">
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-bold text-lg mb-2">How does billing work?</h3>
                <p className="text-gray-700">
                  You'll be charged at the beginning of each billing cycle. You can change your plan at any time - upgrades take effect immediately while downgrades take effect at the end of your current billing period.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-700">
                  Yes! You can cancel your subscription at any time. You'll continue to have access to your current plan until the end of your billing period.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Do unused posts roll over?</h3>
                <p className="text-gray-700">
                  No, your monthly post allowance resets at the beginning of each billing cycle. Make the most of your subscription by using all your available posts each month.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">What happens after the founder pricing ends?</h3>
                <p className="text-gray-700">
                  If you subscribe during our founder pricing period, you'll be locked into these special rates for as long as you maintain an active subscription - even after our regular pricing goes into effect.
                </p>
              </div>
            </div>
            <div className="mt-8 pt-4 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500">
                Need a custom plan for your team or agency? <Link to="/contact" className="text-dogblue hover:underline">Contact our sales team</Link> for enterprise options with higher limits and additional features.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footerdemo />
    </div>
  );
}